import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "./api";

export const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const err = (e) => toast.error(formatApiError(e));
export const SENT = { sent: "Sent", mocked: "Sent (test mode)", skipped: "No address", opted_out: "Opted out", failed: "Failed" };
export const sentToast = (out) => toast.success(Object.entries(out).filter(([k]) => k !== "url").map(([k, v]) => `${k === "sms" ? "Text" : "Email"}: ${SENT[v] || v}`).join(" · ") || "Done");

export async function act(fn, ok) {
  try { const r = await fn(); if (ok) toast.success(ok); return r?.data ?? true; } catch (e) { err(e); return null; }
}

export function useSalesMe() {
  const [me, setMe] = useState(undefined);
  useEffect(() => { api.get("/sales/me").then((r) => setMe(r.data)).catch(() => setMe(null)); }, []);
  return me;
}

export function useLoad(url, deps = []) {
  const [data, setData] = useState(null);
  const load = useCallback(() => (url ? api.get(url).then((r) => setData(r.data)).catch(() => setData([])) : null), [url]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return [data, load];
}

// Live-call state shared with pop-out windows on a second screen.
const CH = "lif-callcenter";
export function useBus(onMsg) {
  const ch = useRef(null);
  useEffect(() => {
    ch.current = new BroadcastChannel(CH);
    ch.current.onmessage = (e) => onMsg?.(e.data);
    return () => ch.current.close();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return useCallback((m) => ch.current?.postMessage(m), []);
}

// Telnyx browser softphone. Falls back gracefully when Telnyx isn't configured.
export function useSoftphone(enabled) {
  const client = useRef(null);
  const [ready, setReady] = useState(false);
  const [call, setCall] = useState(null);
  const [incoming, setIncoming] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let c;
    (async () => {
      try {
        const { TelnyxRTC } = await import("@telnyx/webrtc");
        const { data } = await api.get("/sales/telnyx/token");
        callerId.current = data.caller_id || "";
        c = new TelnyxRTC({ login_token: data.token });
        c.remoteElement = "lif-remote-audio";
        c.on("telnyx.ready", () => setReady(true));
        c.on("telnyx.error", () => setReady(false));
        c.on("telnyx.notification", (n) => {
          if (n.type !== "callUpdate") return;
          const x = n.call;
          if (x.direction === "inbound" && x.state === "ringing") setIncoming(x);
          else if (["hangup", "destroy"].includes(x.state)) { setCall((cur) => (cur?.id === x.id ? null : cur)); setIncoming((cur) => (cur?.id === x.id ? null : cur)); }
          else if (x.state === "active") setCall(x);
        });
        c.connect();
        client.current = c;
      } catch { setReady(false); }
    })();
    return () => c?.disconnect();
  }, [enabled]);
  const callerId = useRef("");
  const dial = (to, from) => {
    const d = String(to || "").replace(/[^\d+]/g, "");
    const dest = d.startsWith("+") ? d : d.length === 10 ? `+1${d}` : d.length === 11 && d[0] === "1" ? `+${d}` : d;
    const x = client.current?.newCall({ destinationNumber: dest, callerNumber: from || callerId.current, audio: true });
    setCall(x);
    return x;
  };
  return { ready, call, incoming, dial, setIncoming, hangup: () => { call?.hangup(); setCall(null); }, mute: (m) => (m ? call?.muteAudio() : call?.unmuteAudio()) };
}
